import { useState } from 'react';
import { Upload, FileSpreadsheet, Download, RefreshCw, CheckCircle, XCircle } from 'lucide-react';
import api from '../services/api';

export default function DataMernis() {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setPreview(null);
      setError('');
    }
  };

  const handlePreview = async () => {
    if (!file) return;
    
    setLoading(true);
    setError('');
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await api.post('/data-mernis/preview', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (res.data.success) {
        setPreview(res.data);
      } else {
        setError(res.data.error || 'Önizleme hatası');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleProcess = async () => {
    if (!file) return;
    
    setProcessing(true);
    setError('');
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await api.post('/data-mernis/process', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        responseType: 'blob'
      });
      
      // Dosyayı indir
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `gsm_eslestirilmis_${file.name}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
    } catch (err) {
      setError('İşlem sırasında hata oluştu');
    } finally {
      setProcessing(false);
    }
  };

  const handleReset = () => {
    setFile(null);
    setPreview(null);
    setError('');
  };

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold text-white mb-6">Data Mernis - GSM Eşleştir</h1>
      
      <div className="bg-gray-800 rounded-xl p-6 mb-6">
        <h2 className="text-lg font-semibold text-white mb-4">Excel Yükle</h2>
        <p className="text-gray-400 mb-4">
          TC sütunu içeren Excel dosyası yükleyin. Sistem TC'lere karşılık gelen GSM numaralarını bulup yeni bir sütun olarak ekleyecek.
        </p>
        
        {/* Upload Area */}
        <div className="border-2 border-dashed border-gray-600 rounded-xl p-8 text-center hover:border-blue-500 transition">
          <input
            type="file"
            accept=".xlsx,.xls"
            onChange={handleFileChange}
            className="hidden"
            id="excel-upload"
          />
          <label htmlFor="excel-upload" className="cursor-pointer">
            {file ? (
              <div className="flex items-center justify-center gap-3">
                <FileSpreadsheet className="w-10 h-10 text-green-500" />
                <div className="text-left">
                  <p className="text-white font-medium">{file.name}</p>
                  <p className="text-gray-400 text-sm">{(file.size / 1024).toFixed(1)} KB</p>
                </div>
              </div>
            ) : (
              <div>
                <Upload className="w-12 h-12 text-gray-500 mx-auto mb-3" />
                <p className="text-gray-400">Excel dosyası seçin veya sürükleyin</p>
                <p className="text-gray-500 text-sm mt-1">.xlsx veya .xls</p>
              </div>
            )}
          </label>
        </div>

        {/* Buttons */}
        {file && (
          <div className="flex gap-3 mt-4">
            <button
              onClick={handlePreview}
              disabled={loading}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white rounded-lg flex items-center gap-2"
            >
              {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />}
              Önizle
            </button>
            <button
              onClick={handleReset}
              className="px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-lg"
            >
              Sıfırla
            </button>
          </div>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-900/50 border border-red-700 text-red-300 px-4 py-3 rounded-lg mb-6 flex items-center gap-2">
          <XCircle className="w-5 h-5" />
          {error}
        </div>
      )}

      {/* Preview Results */}
      {preview && (
        <div className="bg-gray-800 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-500" />
            Dosya Analizi
          </h2>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <StatCard label="Toplam Satır" value={preview.total_rows} />
            <StatCard label="Geçerli TC" value={preview.valid_tc_count} />
            <StatCard label="Tahmini Eşleşme" value={preview.estimated_matches} color="green" />
            <StatCard label="Eşleşme Oranı" value={`%${preview.match_rate}`} color="blue" />
          </div>
          
          <div className="bg-gray-700/50 rounded-lg p-4 mb-4">
            <p className="text-gray-300">
              <strong>{preview.valid_tc_count}</strong> TC'den tahmini <strong className="text-green-400">{preview.estimated_matches}</strong> tanesi GSM veritabanında bulunacak.
            </p>
          </div>
          
          <button
            onClick={handleProcess}
            disabled={processing}
            className="px-6 py-3 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 text-white font-medium rounded-lg flex items-center gap-2"
          >
            {processing ? (
              <>
                <RefreshCw className="w-5 h-5 animate-spin" />
                İşleniyor...
              </>
            ) : (
              <>
                <Download className="w-5 h-5" />
                GSM Eşleştir ve İndir
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, color = 'gray' }) {
  const colors = {
    gray: 'text-white',
    green: 'text-green-400',
    blue: 'text-blue-400'
  };
  
  return (
    <div className="bg-gray-700/50 rounded-lg p-4">
      <div className="text-gray-400 text-sm">{label}</div>
      <div className={`text-2xl font-bold ${colors[color]}`}>{value}</div>
    </div>
  );
}
